import { navigate } from 'gatsby';
import type { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import RoundedButton from '@components/RoundedButton';

import * as ROUTES from '@constants/routes';

import { formatDate } from '@utils/format';

import { useAdminListOperations } from 'generated/admin.generated';

const messages = defineMessages({
  name: {
    id: 'admin.operations.name',
    defaultMessage: 'Name',
  },
  description: {
    id: 'admin.operations.description',
    defaultMessage: 'Description',
  },
  updatedAt: {
    id: 'GLOBAL.updatedAt',
    defaultMessage: 'Updated at',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created At',
  },
  noData: {
    id: 'admin.operations.noData',
    defaultMessage: 'No operations',
  },
  create: {
    id: 'admin.operations.create',
    defaultMessage: 'New Operation',
  },
});

interface RowData {
  id: string;
  created_at: string;
  updated_at: string;
}

function OperationsList() {
  const { formatMessage } = useIntl();
  const { data, loading } = useAdminListOperations();

  const tableData = React.useMemo(
    () =>
      (data?.listOperations ?? []).map<RowData>((operation) => {
        return {
          id: operation.operation,
          updated_at: operation.updated_at,
          created_at: operation.created_at,
        };
      }, []),
    [data]
  );

  const handleRowClick: MUIDataTableOptions['onRowClick'] = (
    _rowData,
    { dataIndex }
  ) => {
    const { id } = tableData[dataIndex];
    navigate(`${ROUTES.ADMIN_MANAGE_OPERATIONS}${id}`);
  };

  const handleCreate = () => {
    navigate(ROUTES.ADMIN_MANAGE_OPERATIONS_CREATE);
  };

  return (
    <ContentLoader loading={loading}>
      <MuiDataTable
        columns={[
          {
            name: 'id',
            className: 'blue-hover',
            label: formatMessage(messages.name),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'created_at',
            className: 'date',
            label: formatMessage(messages.createdAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['created_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
          {
            name: 'updated_at',
            className: 'date',
            label: formatMessage(messages.updatedAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['updated_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
        ]}
        data={tableData}
        options={{
          search: true,
          sort: true,
          filter: true,
          sortOrder: {
            name: 'name',
            direction: 'asc',
          },
          onRowClick: handleRowClick,
          textLabels: {
            body: {
              noMatch: formatMessage(messages.noData),
            },
          },
          customToolbar: () => (
            <RoundedButton
              autoSize
              iconName="plus"
              margin={0}
              onClick={handleCreate}
            >
              {formatMessage(messages.create)}
            </RoundedButton>
          ),
        }}
      />
    </ContentLoader>
  );
}

export default OperationsList;
